import Image from 'next/image';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import MaterialIcon from '@/components/atom/MaterialIcon';
import Refresh from '@/components/atom/icon/Refresh';
import ClickEventLogger from '@/components/common/ClickEventLogger';
import LinkTag from '@/components/common/LinkTag';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import HospitalList from '@/components/templates/f2f/common/HospitalList';
import HospitalRegionFilterModal from '@/components/templates/f2f/common/HospitalRegionFilterModal';
import EmptyNotice from '@/components/templates/f2f/hospital/HospitalDetailPartnerTemplate/EmptyNotice';
import StickyMoveTopButton from '@/components/templates/mobile/f2f/hospital/HospitalsMobileTemplate/StickyMoveTopButton';
import useSetAuthOnScreenFocus from '@/hooks/auth/useSetAuthOnScreenFocus';
import useInfluenzaHospitalListViewModel from '@/hooks/hospital/list/useInfluenzaHospitalListViewModel';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { HospitalInfluenzaFilter } from '@/types/hospital';
import { getDiagnosisStateText } from '@/utils/format';
import { getMobileWebPageName } from '@/utils/logger';

import FreeInfluenzaBottomSheet from './FreeInfluenzaBottomSheet';
import Header from './Header';
import Hospital from './Hospital';
import InfluenzaFilters from './InfluenzaFilters';

const HospitalsInfluenzaTemplate = ({ client }: CustomPageProps) => {
  useSetAuthOnScreenFocus();
  const router = useRouter();
  const pageName = getMobileWebPageName(router);
  const viewModel = useInfluenzaHospitalListViewModel();

  return (
    <TemplateLayout.Wrapper>
      <TemplateLayout.Header useSafeArea>
        <Header
          onClickOpenLocationBottomSheet={viewModel.switchRegionFilter}
          getHeaderAddress={viewModel.getHeaderAddress}
        />
      </TemplateLayout.Header>
      <TemplateLayout.Content>
        <InfluenzaFilters>
          {viewModel.isInfluenzaFiltersSelected && (
            <InfluenzaFilters.Refresh onClick={viewModel.resetInfluenzaFilters}>
              <Refresh />
            </InfluenzaFilters.Refresh>
          )}
          <InfluenzaFilters.Filter
            isSelected={viewModel.isFreeInfluenzaFilterSelected}
            onClick={() =>
              viewModel.toggleInfluenzaFilter(
                HospitalInfluenzaFilter.FreeInfluenza,
              )()
            }
          >
            <span>
              {viewModel.selectedInfluenzaFilters.FreeInfluenza.getTitle()}
            </span>
            <RotationIcon>
              <MaterialIcon
                name="chevron_right"
                size={16}
                color={theme.color.gray700}
              />
            </RotationIcon>
          </InfluenzaFilters.Filter>
        </InfluenzaFilters>
        <HospitalList
          isLoading={viewModel.isLoadingHospitals}
          hasHospitals={!!viewModel.hospitals.length}
          fetchNextPage={viewModel.fetchNextPage}
          hasNextPage={viewModel.hasNextPage}
          style={{ padding: '0 12px' }}
        >
          {viewModel.hospitals.map((hospital, i) => {
            return (
              <LinkTag
                key={`reservable_hospital_${hospital.hospitalPublicId}`}
                onClickNavigateToScreen={() =>
                  viewModel.goToAppHospital(hospital.hospitalPublicId)
                }
                href={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}?type=influenza`}
              >
                <ClickEventLogger
                  pageName={pageName}
                  eventName="click_hospital"
                  payload={{
                    section: 'quick_search',
                    position: i,
                    hospital_id: hospital.hospitalPublicId,
                    status: getDiagnosisStateText(
                      hospital.hospitalOperationStatus,
                    ),
                  }}
                >
                  <Hospital
                    name={hospital.hospitalName}
                    distance={hospital.hospitalDistance}
                    badges={hospital.hospitalBadges}
                    address={hospital.hospitalAddress}
                    isBookable={hospital.isBookable}
                    representativeDepartmentName={
                      hospital.hospitalRepresentativeDepartmentName
                    }
                    diagnosisPrice={hospital.nonBenefit.diagnosisPrice}
                    badgeLabelList={hospital.nonBenefit.labels}
                    imageUrl={hospital.hospitalRepresentativeImageUrl}
                    status={hospital.hospitalOperationStatus}
                  />
                </ClickEventLogger>
              </LinkTag>
            );
          })}
        </HospitalList>
        {!viewModel.isLoadingHospitals && viewModel.hospitals.length === 0 && (
          <EmptyNotice
            icon={
              <Image
                width={40}
                height={40}
                src={'/images/3dGraphics/CryingFace@3x.png'}
                alt="우는 얼굴"
              />
            }
            text={'찾으시는 병원이 없어요'}
            useButton={false}
          />
        )}
        <StickyMoveTopButton />
        {viewModel.isRegionFilterOpened && (
          <HospitalRegionFilterModal
            client={client}
            switchRegionFilter={viewModel.switchRegionFilter}
            addresses={viewModel.addresses}
            selectSavedAddress={viewModel.selectSavedAddress}
            goToAppMyPageAddressScreen={viewModel.goToAppMyPageAddressScreen}
            siDoList={viewModel.siDoList}
            siGunGuList={viewModel.siGunGuList}
            dongList={viewModel.dongList}
            selectedRegion={viewModel.selectedRegion}
            getCurrentLocation={viewModel.getCurrentLocation}
            clickSiDo={viewModel.clickSiDo}
            clickSiGunGu={viewModel.clickSiGunGu}
            clickDong={viewModel.clickDong}
            isOpened={viewModel.isRegionFilterOpened}
            isSejong={viewModel.isSejong}
          />
        )}
        {viewModel.isInfluenzaFiltersOpen.FreeInfluenza && (
          <FreeInfluenzaBottomSheet
            {...viewModel.getFreeInfluenzaFiltersProps()}
          />
        )}
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default HospitalsInfluenzaTemplate;

const RotationIcon = styled.div`
  transform: rotate(90deg);
`;
